'use client'

import Script from 'next/script'
import { useCallback, useEffect, useRef } from 'react'

import { useGoogleTagManager } from '../../../../lib/hooks/useGoogleTagManager/useGoogleTagManager'
import { sessionStorageKeys } from '../../../_config/Session.config'
import { useConsentPolicy } from '../../../_providers/ConsentPolicyProvider.client'
import { useLocalization } from '../../../_providers/LocalizationProvider.client'
import { useUser } from '../../../_providers/UserProvider.client'

export const GtmScript = () => {
  const { consentPolicy, gdprCountry } = useConsentPolicy()
  const { performance, targeting, functional, social } = consentPolicy
  const currentUserEmailRef = useRef<string | undefined>('INITIAL')
  const { getDataLayer, pushCustomerData, pushClearCustomerData, pushGtmData } =
    useGoogleTagManager()
  const { user, userStatus } = useUser()
  const { region, currency, languageGroup } = useLocalization()

  const updateLocalizationData = useCallback(() => {
    if (gdprCountry) {
      pushGtmData({
        event: 'localization_data',
        currency,
        FIGSRegion: region.id,
        gdprCountry,
        language: languageGroup,
      })
    } else {
      pushGtmData({
        event: 'localization_data',
        currency,
        FIGSRegion: region.id,
        language: languageGroup,
      })
    }
  }, [pushGtmData, currency, region, gdprCountry, languageGroup])

  const updateUser = useCallback(() => {
    if (userStatus === 'pending') return
    if (currentUserEmailRef.current === user?.email) return
    currentUserEmailRef.current = user?.email

    if (user) {
      pushCustomerData({
        shopifyId: user.shopifyId ?? undefined,
        email: user.email,
        ordersCount: user.profile.ordersCount,
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        firstName: user.profile.firstName ?? undefined,
        lastName: user.profile.lastName ?? undefined,
        phone: user.profile.phone ?? undefined,
      })
    } else {
      pushClearCustomerData()
    }
  }, [pushClearCustomerData, pushCustomerData, user, userStatus])

  const updateLandingPage = useCallback(() => {
    const alreadyPushed = getDataLayer().some(dataLayerEntry => {
      return Boolean(dataLayerEntry?.originalLocation)
    })

    if (alreadyPushed) return

    const currentLocation =
      window.document.location.protocol +
      '//' +
      window.document.location.host +
      window.document.location.pathname +
      window.document.location.search
    const originalLocation =
      window.sessionStorage.getItem(sessionStorageKeys.landingPage) ?? currentLocation
    window.sessionStorage.setItem(sessionStorageKeys.landingPage, originalLocation)

    pushGtmData({
      originalLocation,
    })
  }, [getDataLayer, pushGtmData])

  useEffect(() => {
    updateUser()
  }, [updateUser])

  useEffect(() => {
    updateLandingPage()
  }, [updateLandingPage])

  useEffect(() => {
    updateLocalizationData()
  }, [updateLocalizationData])

  const containerId = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID
  if (
    !containerId ||
    performance !== 'allowed' ||
    social !== 'allowed' ||
    targeting !== 'allowed' ||
    functional !== 'allowed'
  ) {
    return null
  }

  return (
    <Script
      id='gtm-script'
      strategy='lazyOnload' // Marketing is set to lazy on load
      dangerouslySetInnerHTML={{
        __html: `
            (function(w,d,s,l,i){
              w[l]=w[l]||[];w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
              var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;j.src='https://www.googletagmanager.com/gtm.js?gtm_cookies_win=x&id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${containerId}');
          `,
      }}
    />
  )
}
